<template>
  <div ref="chartDetail" class="show-chart-dashboard" :style="getChartPosition">
    <lego-dashboard-item :x="0" :y="0" :w="1.0" :h="1.0" :col="1" :gap="12" :row="1">
      <div ref="mainChart" class="main-chart">
        <div class="chart-title">
          <div @click="toggleDetailChart" class="chart-title_text">
            <c-label :ellipsis="true">{{ selectedMetric.title }}</c-label>
          </div>
          <div class="chart-max-value" v-if="selectedMetric.chartList.length > 0">
            <div class="until" @mouseenter="onHover" @mouseleave="onLeave">
              <span>{{ selectedMetric.maxValue }}</span>

              <span class="metric-unit">{{ selectedMetric.displayUnit }}</span>
            </div>
          </div>
        </div>
        <div
          v-if="
            selectedMetric &&
            selectedMetric.chartList &&
            selectedMetric.chartList.length > 0 &&
            !chartLoading
          "
          class="chart-detail-content"
        >
          <c-chart
            ref="cChartRef"
            :items="selectedMetric.chartList"
            :type="selectedMetric.chartType"
            :opt="selectedMetric.chartOption"
            :exopt="selectedMetric.chartExopt"
            cursor="default"
            :silent="true"
          />
        </div>
        <div v-if="selectedMetric.chartList.length == 0 && !chartLoading" class="no-data-chart">
          <div class="empty-content">
            <lego-icon iconType="picto" icon="widget_nodata" :size="37" color="#d2d3d6" />
            <div style="margin-top: 6px">{{ $t('label.noData') }}</div>
          </div>
        </div>
        <lego-progress
          v-if="chartLoading"
          style="margin-top: 64px"
          class="progress"
          size="medium"
        ></lego-progress>
      </div>
    </lego-dashboard-item>
  </div>
</template>

<script setup>
import {
  computed,
  ref,
  defineProps,
  getCurrentInstance,
  reactive,
  onMounted,
  onBeforeUnmount,
  beforeUnmount
} from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import legoEventBus from '../../../lego-component/utils/eventBus/eventBus';

const cChartRef = ref(null);
const props = defineProps({
  targetElement: {
    type: HTMLElement,
    default: null
  },
  metric: {
    type: Object,
    default: {}
  },
  resourceId: {
    type: String,
    default: ''
  },
  productTypeCode: {
    type: String,
    default: ''
  }
});
const { t, locale } = useI18n();
const { $http, $util, $filters } = getCurrentInstance().appContext.config.globalProperties;
const _chartWidth = ref(300);
const _chartHeight = ref(194);
const _margin = ref(6);
const chartDetail = ref(null);
const selectedMetric = reactive({
  chartList: []
});
const chartThemeColor = ref([
  '#20BAC2',
  '#6952DB',
  '#1E5FCB',
  '#0EBB59',
  '#FFC14A',
  '#D7C7FE',
  '#FD9C94',
  '#5C0931',
  '#8EDDCF',
  '#166440',
  '#E6E6E6',
  '#7394FF',
  '#8B21A6',
  '#BDBDBD',
  '#F56813',
  '#90DCE0',
  '#B4A9ED',
  '#808DAE',
  '#87DDAC',
  '#FFE0A5'
]);
const chartLoading = ref(true);
onMounted(() => {
  legoEventBus.$on('click-outside', () => onLeave());
  window.addEventListener('resize', setChartPosition);
  getMetricData();
});

onBeforeUnmount(() => {
  legoEventBus.$off('click-outside', () => onLeave());
  window.removeEventListener('resize', setChartPosition);
});

const getChartPosition = computed(() => handleChartPosition());
const setChartPosition = () => {
  getChartPosition.value = handleChartPosition();
};

const handleChartPosition = () => {
  let anchorStyle = { width: `${_chartWidth.value}px`, height: `${_chartHeight.value}px` };
  const bodyRect = document.body.getBoundingClientRect();
  const targetRect = props.targetElement.getBoundingClientRect();
  if (bodyRect.x + bodyRect.width - targetRect.x - targetRect.width < _chartWidth.value / 2) {
    anchorStyle.left = `calc(${targetRect.left}px + ${targetRect.width}px - ${_chartWidth.value}px - ${bodyRect.left}px) `;
  } else {
    anchorStyle.left = `calc(${targetRect.left}px + ${targetRect.width / 2}px - ${
      _chartWidth.value / 2
    }px - ${bodyRect.left}px)`;
  }
  if (bodyRect.y + bodyRect.height - targetRect.y < _chartHeight.value + targetRect.height) {
    anchorStyle.top = `calc(${targetRect.top}px - ${_chartHeight.value}px`;
  } else {
    anchorStyle.top = `calc(${targetRect.top}px + ${targetRect.height}px`;
  }
  return anchorStyle;
};
const closeDetailChart = () => {
  cChartRef.value.$refs.chart.dispatchAction({
    type: 'hideTip'
  });
};
const xLabelFormatter = (value) => {
  return $util.getDateFormat(value, '5m');
};
const buildOption = () => {
  let options = {
    grid: {
      left: 5,
      top: 34,
      right: 15,
      bottom: 0,
      containLabel: true
    },
    legend: {
      bottom: 16,
      show: false
    },
    xAxis: [
      {
        type: 'time',
        splitNumber: 5,
        boundaryGap: ['1%', '1%'],
        nameGap: -11,
        nameLocation: 'center',
        axisLabel: {
          fontSize: 11,
          formatter: xLabelFormatter,
          hideOverlap: true
        }
      }
    ],
    yAxis: {
      name: '',
      axisLabel: {
        formatter: '{value}'
      }
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#fff',
      textStyle: {
        color: '#000'
      },
      axisPointer: {
        type: 'line',
        label: {
          formatter: (params) => {
            let label = params.value;
            if (params.axisDimension == 'x') {
              label = moment(label).format('YYYY-MM-DD HH:mm:ss');
            } else if (params.axisDimension == 'y') {
              label = $filters.comma(Math.ceil(label));
            }
            return label;
          }
        }
      },
      className: 'toolbar-scrollbar',
      confine: false,
      renderMode: 'html',
      extraCssText: 'max-height:290px; overflow:auto;',
      triggerOn: 'mousemove',
      enterable: true,
      transitionDuration: 0.8,
      appendToBody: true,
      position: (point, params, dom, rect, size) => {
        return $util.getPositionTooltip(point, size);
      }
    }
  };
  return options;
};

const initCharInfo = () => {
  selectedMetric.chartOption = buildOption();
  selectedMetric.chartType = 'line';
  selectedMetric.title = props.metric['metricTitle'];
  selectedMetric.key = props.metric['metricKey'];
  selectedMetric.zoom = false;
};
const apiGetMetricData = async (params, productTypeCode) => {
  return $http.product.post('/v1/console/metric-data', params, false, false, {
    headers: { 'X-ResourceType': productTypeCode }
  });
};

const getMetricData = async (k8sData) => {
  let apiParams = {
    metricDataConditionConsole: {
      metricKey: props.metric && props.metric.metricKey,
      objectType: (props.metric && props.metric.objectType) || '',
      productResourceInfosConsole: [
        {
          productResourceId: props.resourceId,
          objectList: props.metricObjectList || []
        }
      ]
    },
    queryStartDt: moment(new Date()).add(-30, 'minute').toISOString(),
    queryEndDt: moment(new Date()).toISOString()
  };
  if (k8sData) {
    apiParams = { ...apiParams, ...k8sData };
  }

  let params = $util.deepCopy(apiParams);

  await apiGetMetricData(params, props.productTypeCode)
    .then((data) => {
      selectedMetric.chartList = [];
      selectedMetric.metricData = [];
      initCharInfo();

      const { contents: metricData } = data;
      if (!metricData.length) return;

      selectedMetric.metricData = [...metricData];

      const { metricUnit, fixedUnit, displayUnit } = metricData[0];
      const listMaxValues = metricData.map((e) =>
        e.perfData.reduce((acc, cur) => Math.max(acc, cur.value), -Infinity)
      );
      const maxValue = Math.max(...listMaxValues);
      const isAbleToConvert = $util.isUnitAbleToConvert(metricUnit);
      const { unit: highestUnit } = isAbleToConvert
        ? $util.convertToHigherUnit(maxValue, metricUnit)
        : $util.convertNumberSymbol(maxValue, metricUnit);

      const usageUnit = fixedUnit !== null ? fixedUnit : highestUnit;

      let dummy = {
        startDt: '',
        endDt: '',
        queryStartDt: moment(new Date()).add(-30, 'minute').toISOString(),
        queryEndDt: moment(new Date()).toISOString(),
        diffMSec: '5m'
      };

      let result = [];
      let tooltipChart = {
        date: '',
        maxValueChart: []
      };

      selectedMetric.chartOption.tooltip = {
        ...selectedMetric.chartOption.tooltip,
        enterable: data.contents.length > 12
      };

      data.contents.forEach((item, idx) => {
        dummy.startDt = item.perfData[0].ts;
        dummy.endDt = item.perfData[item.perfData.length - 1].ts;
        tooltipChart.date = moment(item.perfData[item.perfData.length - 1].ts).format(
          'YYYY-MM-DD HH:mm:ss'
        );

        let timeDiff = moment(new Date()).diff(moment(new Date()).add(-30, 'minute'), 'minutes');

        let datas = [];
        let dataIndex = item.perfData.length;

        if (item.perfData.length) {
          datas = item.perfData.map((el) => {
            const numVal = Number(el.value);
            const { value } = isAbleToConvert
              ? $util.convertToSpecificUnit(numVal, metricUnit, usageUnit)
              : $util.toSpecificNumberSymbol(numVal, usageUnit, metricUnit);
            const roundedValue = Number(value.toFixed(2));
            return [el.ts, roundedValue];
          });
          const lastValue = datas[datas.length - 1][1];
          const valueUnit = !isAbleToConvert && usageUnit ? usageUnit : '';
          selectedMetric.maxValue = $util.formatNumberInThousands(lastValue) + valueUnit;
          selectedMetric.displayUnit = isAbleToConvert ? usageUnit : displayUnit;

          datas[datas.length - 1] = {
            value: datas[datas.length - 1],
            symbol: 'emptyCircle',
            symbolSize: '7'
          };

          tooltipChart.maxValueChart.push({
            color: chartThemeColor.value[idx],
            name: item.objectDisplayName
              ? item.productName +
                '.' +
                item.objectDisplayName +
                '(' +
                t(`label.${item.statisticsType.toLowerCase()}`) +
                ')'
              : item.productName + '(' + t(`label.${item.statisticsType.toLowerCase()}`) + ')',
            value: lastValue
          });

          if (dummy.queryStartDt < dummy.startDt) {
            datas.unshift([dummy.queryStartDt, null]);
            dataIndex += 1;
          }
          if (dummy.queryEndDt > dummy.endDt) {
            datas.push([dummy.queryEndDt, null]);
            dataIndex += 1;
          }
        } else {
          datas = [
            [dummy.queryStartDt, null],
            [dummy.queryEndDt, null]
          ];
          dataIndex = 2;
        }

        result.push({
          data: datas,
          name: item.objectDisplayName,
          timeDiff: timeDiff,
          dataIndex
        });
      });

      const labelUnit = usageUnit == displayUnit ? '' : usageUnit;
      const yAxisName = `(${displayUnit || metricUnit})`;
      const yAxisLabel = labelUnit ? `{value} ${labelUnit}` : `{value}`;

      selectedMetric.chartOption.yAxis = {
        name: yAxisName,
        axisLabel: {
          formatter: yAxisLabel
        }
      };

      selectedMetric.chartList = result;
      selectedMetric.tooltipChart = tooltipChart;
    })
    .finally(() => {
      chartLoading.value = false;
    });
};
const toggleDetailChart = () => {};
const onHover = () => {
  if (!cChartRef.value) return;
  cChartRef.value.$refs.chart.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: selectedMetric.chartList[0].dataIndex - 1
  });
};
const onLeave = () => {
  if (!cChartRef.value) return;

  cChartRef.value.$refs.chart.dispatchAction({
    type: 'hideTip'
  });
  cChartRef.value.$refs.chart.dispatchAction({
    type: 'showTip',
    seriesIndex: 0,
    dataIndex: -1
  });
};
const formatMaxValue = (value, metricUnit) => {
  if (metricUnit === '%') {
    return Number(value).toFixed(2);
  }
  return value;
};
</script>

<style scoped lang="scss">
.show-chart-dashboard {
  z-index: 1003;
  position: absolute;
  background-color: rgba(255, 255, 255, 1);
  border: 1px solid #e3e4e8;
  box-shadow: 0px 8px 15px 2px #22252a26, 0px 0px 4px 0px #22252a33, 0px -1px 4px 0px #22252a26;
  border-radius: 4px;
}

.main-chart {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 16px;

  .no-data-chart {
    display: flex;
    justify-content: center;
    margin-top: 48px;

    .empty-content {
      text-align: center;
    }
  }
}

.chart-title {
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  &_text {
    max-width: 200px;
  }

  :deep .c-label {
    font-size: 12px !important;
    font-weight: 700 !important;
    color: #2f3237 !important;
  }

  .chart-max-value {
    display: flex;

    span {
      font-size: 13px;
      line-height: 16px;
      font-weight: 500;
      color: #2f3237;
    }

    .metric-unit {
      margin-left: 4px;
    }
  }
}

.chart-detail {
  &__main {
    box-shadow: 0px 8px 15px 2px #22252a26, 0px 0px 4px 0px #22252a33, 0px -1px 4px 0px #22252a26;
    border-radius: 4px;
  }
}

.detail-chart {
  position: absolute;
  width: 1210px;
  height: 320px;
  z-index: 2;
  background-color: #ffffff;
}

.chart-thumnail-item {
  padding: 0;
  border: 1px solid #e3e3e3;
}

.chart-detail-item {
  padding: 8px 16px 16px 16px;
  border: 1px solid #e3e3e3;

  .chart-detail-content {
    display: flex;
    align-items: flex-end;
    height: 100%;
  }

  .chart-title {
    .chart-title_text > .c-label {
      font-size: 14px !important;
    }

    .chart-max-value > .until > span {
      font-size: 16px !important;
    }
  }
}

.chart-thumnail-title {
  padding: 10px;
}

.chart-detail-title {
  padding: 10px 10px 0 10px;
}

.chart-detail-content {
  height: 100%;
}

.chart-detail {
  width: 100%;
}

.chart-title {
  .chart-title_text {
    overflow: hidden;
    max-width: unset;
    & > .active-title {
      color: #2c5ad8 !important;
    }
  }
}

.footer-detail {
  display: flex;
  flex-direction: row-reverse;
  padding-top: 16px;
  border-top: 1px solid #e3e4e8;
}
</style>
